import type { Infer } from 'spacetimedb';
import { AdminMapCatalogRow, MyAdminStatusRow, reducers } from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type { ReducerClient } from '../core/ReducerClient';
import type { SubscriptionFactory, SubscriptionHandle } from '../core/SubscriptionFactory';
import type { AdminMapSummary, AdminStatusSnapshot, Logger } from '../types';
import type { PathCell } from '../../model/gamestate/GameState';

type AdminMapCatalogTableRow = Infer<typeof AdminMapCatalogRow>;
type MyAdminStatusTableRow = Infer<typeof MyAdminStatusRow>;

export class AdminModule {
  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly reducerClient: ReducerClient,
    private readonly subscriptionFactory: SubscriptionFactory,
    private readonly logger: Logger = () => undefined
  ) { }

  public async placeNpc(
    mapId: string,
    worldX: number,
    worldY: number,
    npcType: string,
    metadataJson: string
  ): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_place_npc',
      paramsType: reducers.adminPlaceNpc.paramsType,
      args: {
        mapId,
        worldX,
        worldY,
        npcType,
        metadataJson
      },
      onEvent: 'onAdminPlaceNpc',
      offEvent: 'removeOnAdminPlaceNpc',
      timeoutMessage: 'Place NPC timed out',
      failureMessage: 'Failed to place NPC'
    });
  }

  public async removeNpc(mapId: string, worldX: number, worldY: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_remove_npc',
      paramsType: reducers.adminRemoveNpc.paramsType,
      args: {
        mapId,
        worldX,
        worldY
      },
      onEvent: 'onAdminRemoveNpc',
      offEvent: 'removeOnAdminRemoveNpc',
      timeoutMessage: 'Remove NPC timed out',
      failureMessage: 'Failed to remove NPC'
    });
  }

  public async addMapObject(
    mapId: string,
    objectId: string,
    asset: string,
    worldX: number,
    worldY: number,
    offsetX: number,
    offsetY: number,
    sizeMultiplier: number,
    flipHorizontal: boolean,
    flipVertical: boolean,
    rotationSteps: number,
    layer: string,
  ): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_add_map_object',
      paramsType: reducers.adminAddMapObject.paramsType,
      args: {
        mapId,
        objectId,
        asset,
        worldX,
        worldY,
        offsetX,
        offsetY,
        sizeMultiplier,
        flipHorizontal,
        flipVertical,
        rotationSteps,
        layer,
      },
      onEvent: 'onAdminAddMapObject',
      offEvent: 'removeOnAdminAddMapObject',
      timeoutMessage: 'Add map object timed out',
      failureMessage: 'Failed to add map object'
    });
  }

  public async removeMapObject(mapId: string, objectId: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_remove_map_object',
      paramsType: reducers.adminRemoveMapObject.paramsType,
      args: {
        mapId,
        objectId,
      },
      onEvent: 'onAdminRemoveMapObject',
      offEvent: 'removeOnAdminRemoveMapObject',
      timeoutMessage: 'Remove map object timed out',
      failureMessage: 'Failed to remove map object'
    });
  }

  public async setMapPathCell(
    mapId: string,
    atlasId: string,
    worldX: number,
    worldY: number,
    cell: PathCell | null,
  ): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_set_map_path_cell',
      paramsType: reducers.adminSetMapPathCell.paramsType,
      args: {
        mapId,
        atlasId,
        worldX,
        worldY,
        mode: cell?.mode ?? 'erase',
        variantId: cell?.mode === 'fixed' ? cell.variantId : '',
        flipHorizontal: cell?.mode === 'fixed' && cell.flipHorizontal === true,
        flipVertical: cell?.mode === 'fixed' && cell.flipVertical === true,
        rotationSteps: cell?.mode === 'fixed' ? cell.rotationSteps ?? 0 : 0,
      },
      onEvent: 'onAdminSetMapPathCell',
      offEvent: 'removeOnAdminSetMapPathCell',
      timeoutMessage: 'Set map path cell timed out',
      failureMessage: 'Failed to update map path cell'
    });
  }

  public async setMapPathBorderContinuation(
    mapId: string,
    atlasId: string,
    worldX: number,
    worldY: number,
    active: boolean,
  ): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_set_map_path_border_continuation',
      paramsType: reducers.adminSetMapPathBorderContinuation.paramsType,
      args: {
        mapId,
        atlasId,
        worldX,
        worldY,
        active,
      },
      onEvent: 'onAdminSetMapPathBorderContinuation',
      offEvent: 'removeOnAdminSetMapPathBorderContinuation',
      timeoutMessage: 'Set map path border continuation timed out',
      failureMessage: 'Failed to update map path border continuation'
    });
  }

  public async disconnectPlayerSession(query: string): Promise<void> {
    const trimmed = query.trim();
    if (!trimmed) {
      throw new Error('Player name or ID is required');
    }

    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_disconnect_player_session',
      paramsType: reducers.adminDisconnectPlayerSession.paramsType,
      args: { query: trimmed },
      onEvent: 'onAdminDisconnectPlayerSession',
      offEvent: 'removeOnAdminDisconnectPlayerSession',
      matchArgs: (args) => typeof args.query === 'string' && args.query.trim() === trimmed,
      timeoutMessage: 'Disconnect player timed out',
      failureMessage: 'Failed to disconnect player session'
    });
  }

  public async bringPlayerHere(query: string): Promise<void> {
    const trimmed = query.trim();
    if (!trimmed) {
      throw new Error('Player name or ID is required');
    }

    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_bring_player_here',
      paramsType: reducers.adminBringPlayerHere.paramsType,
      args: { query: trimmed },
      onEvent: 'onAdminBringPlayerHere',
      offEvent: 'removeOnAdminBringPlayerHere',
      matchArgs: (args) => typeof args.query === 'string' && args.query.trim() === trimmed,
      timeoutMessage: 'Bring player timed out',
      failureMessage: 'Failed to bring player'
    });
  }

  public async createMap(
    mapId: string,
    name: string,
    width: number,
    height: number,
    gridTypeOrSeedOffsetX: string | number,
    seedOffsetXOrSeedOffsetY: number = 0,
    seedOffsetY: number = 0
  ): Promise<void> {
    const normalized = this.normalizeCreateMapArgs(
      gridTypeOrSeedOffsetX,
      seedOffsetXOrSeedOffsetY,
      seedOffsetY
    );

    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_create_map',
      paramsType: reducers.adminCreateMap.paramsType,
      args: {
        mapId,
        name,
        width,
        height,
        gridType: normalized.gridType,
        seedOffsetX: normalized.seedOffsetX,
        seedOffsetY: normalized.seedOffsetY
      },
      onEvent: 'onAdminCreateMap',
      offEvent: 'removeOnAdminCreateMap',
      timeoutMessage: 'Create map timed out',
      failureMessage: 'Failed to create map'
    });
  }

  private normalizeCreateMapArgs(
    gridTypeOrSeedOffsetX: string | number,
    seedOffsetXOrSeedOffsetY: number,
    seedOffsetY: number
  ): { gridType: string; seedOffsetX: number; seedOffsetY: number } {
    if (typeof gridTypeOrSeedOffsetX === 'string') {
      return {
        gridType: gridTypeOrSeedOffsetX,
        seedOffsetX: seedOffsetXOrSeedOffsetY,
        seedOffsetY,
      };
    }

    return {
      gridType: 'hex',
      seedOffsetX: gridTypeOrSeedOffsetX,
      seedOffsetY: seedOffsetXOrSeedOffsetY,
    };
  }

  public async createInstance(mapId: string, channelId: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_create_instance',
      paramsType: reducers.adminCreateInstance.paramsType,
      args: { mapId, channelId },
      onEvent: 'onAdminCreateInstance',
      offEvent: 'removeOnAdminCreateInstance',
      timeoutMessage: 'Create instance timed out',
      failureMessage: 'Failed to create instance'
    });
  }

  public async addPortal(
    mapId: string,
    portalX: number,
    portalY: number,
    destMapId: string,
    destX: number,
    destY: number
  ): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_add_portal',
      paramsType: reducers.adminAddPortal.paramsType,
      args: {
        mapId,
        portalX,
        portalY,
        destMapId,
        destX,
        destY
      },
      onEvent: 'onAdminAddPortal',
      offEvent: 'removeOnAdminAddPortal',
      timeoutMessage: 'Add portal timed out',
      failureMessage: 'Failed to add portal'
    });
  }

  public async addPortalAuto(
    mapId: string,
    portalX: number,
    portalY: number,
    destMapId: string,
  ): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_add_portal_auto',
      paramsType: reducers.adminAddPortalAuto.paramsType,
      args: {
        mapId,
        portalX,
        portalY,
        destMapId,
      },
      onEvent: 'onAdminAddPortalAuto',
      offEvent: 'removeOnAdminAddPortalAuto',
      timeoutMessage: 'Add auto portal timed out',
      failureMessage: 'Failed to add auto portal'
    });
  }

  public async removePortal(mapId: string, portalX: number, portalY: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_remove_portal',
      paramsType: reducers.adminRemovePortal.paramsType,
      args: {
        mapId,
        portalX,
        portalY,
      },
      onEvent: 'onAdminRemovePortal',
      offEvent: 'removeOnAdminRemovePortal',
      timeoutMessage: 'Remove portal timed out',
      failureMessage: 'Failed to remove portal'
    });
  }

  public async wipeAndReloadMaps(): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'force_reseed_maps',
      paramsType: reducers.forceReseedMaps.paramsType,
      args: {},
      onEvent: 'onForceReseedMaps',
      offEvent: 'removeOnForceReseedMaps',
      timeoutMessage: 'Wipe and reload maps timed out',
      failureMessage: 'Failed to wipe and reload maps'
    });
  }

  public async reloadMapAsset(mapId: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_reload_map_asset',
      paramsType: reducers.adminReloadMapAsset.paramsType,
      args: { mapId },
      onEvent: 'onAdminReloadMapAsset',
      offEvent: 'removeOnAdminReloadMapAsset',
      timeoutMessage: 'Reload map asset timed out',
      failureMessage: 'Failed to reload map asset'
    });
  }

  public async importMapJson(mapJson: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_import_map_json',
      paramsType: reducers.adminImportMapJson.paramsType,
      args: { mapJson },
      onEvent: 'onAdminImportMapJson',
      offEvent: 'removeOnAdminImportMapJson',
      timeoutMessage: 'Import map JSON timed out',
      failureMessage: 'Failed to import map JSON'
    });
  }

  public async deleteMap(mapId: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_delete_map',
      paramsType: reducers.adminDeleteMap.paramsType,
      args: { mapId },
      onEvent: 'onAdminDeleteMap',
      offEvent: 'removeOnAdminDeleteMap',
      timeoutMessage: 'Delete map timed out',
      failureMessage: 'Failed to delete map'
    });
  }

  public async listMaps(): Promise<AdminMapSummary[]> {
    return this.subscriptionFactory.runOneTimeQuery({
      sql: 'SELECT * FROM admin_map_catalog',
      getTable: (db) => db.adminMapCatalog,
      mapRow: (row) => this.mapCatalogRow(row as unknown as AdminMapCatalogTableRow),
      sortFn: (a, b) => {
        const byKind = a.mapKind.localeCompare(b.mapKind);
        if (byKind !== 0) {
          return byKind;
        }
        return a.mapId.localeCompare(b.mapId);
      }
    });
  }

  public async getMyAdminStatus(): Promise<AdminStatusSnapshot | null> {
    const results = await this.subscriptionFactory.runOneTimeQuery({
      sql: 'SELECT * FROM my_admin_status',
      getTable: (db) => db.myAdminStatus,
      mapRow: (row) => this.mapAdminStatusRow(row as unknown as MyAdminStatusTableRow),
    });
    return results[0] ?? null;
  }

  public async subscribeToMyAdminStatus(
    options: { onUpdate: (status: AdminStatusSnapshot | null) => void }
  ): Promise<SubscriptionHandle> {
    return this.subscriptionFactory.createSingleRowSubscription({
      sql: 'SELECT * FROM my_admin_status',
      getTable: (db) => db.myAdminStatus,
      mapRow: (row) => this.mapAdminStatusRow(row as unknown as MyAdminStatusTableRow),
    }, options.onUpdate);
  }

  public async setMapTile(
    mapId: string,
    worldX: number,
    worldY: number,
    terrainType: string,
    biomeType: string
  ): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_set_map_tile',
      paramsType: reducers.adminSetMapTile.paramsType,
      args: {
        mapId,
        worldX,
        worldY,
        terrainType,
        biomeType
      },
      onEvent: 'onAdminSetMapTile',
      offEvent: 'removeOnAdminSetMapTile',
      timeoutMessage: 'Set map tile timed out',
      failureMessage: 'Failed to edit map tile'
    });
  }

  public async setMapBiomeRegion(
    mapId: string,
    worldX: number,
    worldY: number,
    width: number,
    height: number,
    biomeType: string
  ): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_set_map_biome_region',
      paramsType: reducers.adminSetMapBiomeRegion.paramsType,
      args: {
        mapId,
        worldX,
        worldY,
        width,
        height,
        biomeType
      },
      onEvent: 'onAdminSetMapBiomeRegion',
      offEvent: 'removeOnAdminSetMapBiomeRegion',
      timeoutMessage: 'Set biome region timed out',
      failureMessage: 'Failed to edit biome region'
    });
  }

  public async setMapDefaultBiome(mapId: string, biomeType: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'admin_set_map_default_biome',
      paramsType: reducers.adminSetMapDefaultBiome.paramsType,
      args: {
        mapId,
        biomeType,
      },
      onEvent: 'onAdminSetMapDefaultBiome',
      offEvent: 'removeOnAdminSetMapDefaultBiome',
      timeoutMessage: 'Set default terrain timed out',
      failureMessage: 'Failed to update map default terrain'
    });
  }

  private mapCatalogRow(row: AdminMapCatalogTableRow): AdminMapSummary {
    return {
      mapId: row.mapId,
      name: row.name,
      mapKind: row.mapKind,
      gridType: row.gridType,
      width: Number(row.width),
      height: Number(row.height)
    };
  }

  private mapAdminStatusRow(row: MyAdminStatusTableRow): AdminStatusSnapshot {
    return {
      role: row.role,
      isAdmin: row.isAdmin,
      isSuperAdmin: row.isSuperAdmin,
    };
  }
}
